<template>
  <div class="stock-statistics">
    <div class="page-header">
      <h2>出入库统计</h2>
    </div>
    
    <!-- 汇总统计卡片 -->
    <a-card class="summary-card" :loading="summaryLoading">
      <a-row :gutter="24">
        <!-- 入库总量 -->
        <a-col :span="12">
          <div class="stat-section">
            <a-row :gutter="16">
              <a-col :span="12">
                <a-statistic 
                  title="入库总量" 
                  :value="summaryData.inStockSum || 0" 
                  :value-style="{ color: '#52c41a', fontSize: '28px', fontWeight: 'bold' }"
                  suffix="件"
                />
              </a-col>
              <a-col :span="12">
                <div class="stat-details">
                  <a-statistic 
                    title="生产入库" 
                    :value="summaryData.productionSum || 0" 
                    :value-style="{ color: '#52c41a', fontSize: '16px', fontWeight: 'bold' }"
                    suffix="件"
                    style="margin-bottom: 8px;"
                  />
                  <a-statistic 
                    title="退货入库" 
                    :value="summaryData.returnSum || 0" 
                    :value-style="{ color: '#fa8c16', fontSize: '16px', fontWeight: 'bold' }"
                    suffix="件"
                    style="margin-bottom: 8px;"
                  />
                  <a-statistic 
                    title="其他入库" 
                    :value="summaryData.inOtherSum || 0" 
                    :value-style="{ color: '#1890ff', fontSize: '16px', fontWeight: 'bold' }"
                    suffix="件"
                  />
                </div>
              </a-col>
            </a-row>
          </div>
        </a-col>
        
        <!-- 出库总量 -->
        <a-col :span="8">
          <div class="stat-section">
            <a-row :gutter="16">
              <a-col :span="12">
                <a-statistic
                  title="出库总量"
                  :value="summaryData.outStockSum || 0"
                  :value-style="{ color: '#ff4d4f', fontSize: '28px', fontWeight: 'bold' }"
                  suffix="件"
                />
              </a-col>
              <a-col :span="12">
                <div class="stat-details">
                  <a-statistic
                    title="销售出库"
                    :value="summaryData.salesSum || 0"
                    :value-style="{ color: '#ff4d4f', fontSize: '16px', fontWeight: 'bold' }"
                    suffix="件"
                    style="margin-bottom: 8px;"
                  />
                  <a-statistic
                    title="其他出库"
                    :value="summaryData.outOtherSum || 0"
                    :value-style="{ color: '#722ed1', fontSize: '16px', fontWeight: 'bold' }"
                    suffix="件"
                  />
                </div>
              </a-col>
            </a-row>
          </div>
        </a-col>

        <!-- 净销售数据 -->
        <a-col :span="4">
          <div class="stat-section">
            <a-statistic
              title="净销售总量"
              :value="(summaryData.salesSum || 0) - (summaryData.returnSum || 0)"
              :value-style="{ color: ((summaryData.salesSum || 0) - (summaryData.returnSum || 0)) > 0 ? '#52c41a' : ((summaryData.salesSum || 0) - (summaryData.returnSum || 0)) === 0 ? '#666' : '#ff4d4f', fontSize: '28px', fontWeight: 'bold' }"
              suffix="件"
            />
            <div style="font-size: 12px; color: #999; margin-top: 8px;">
              销售 - 退货
            </div>
          </div>
        </a-col>
      </a-row>
    </a-card>
    
    <a-card class="filter-card">
      <a-form
        :model="searchForm"
        layout="inline"
        @finish="handleSearch"
        class="search-form"
      >
        <a-form-item label="商品名称" name="productName">
          <a-input
            v-model:value="searchForm.productName"
            placeholder="请输入商品名称"
            style="width: 200px"
            allow-clear
          />
        </a-form-item>
        
        <a-form-item label="原因类型" name="reasonType">
          <a-select
            v-model:value="searchForm.reasonType"
            placeholder="请选择原因类型"
            style="width: 140px"
            allow-clear
          >
            <a-select-option value="production">生产入库</a-select-option>
            <a-select-option value="return">退货入库</a-select-option>
            <a-select-option value="other_in">其他入库</a-select-option>
            <a-select-option value="sales">销售出库</a-select-option>
            <a-select-option value="other_out">其他出库</a-select-option>
          </a-select>
        </a-form-item>
        
        <a-form-item label="统计日期" name="dateRange">
          <a-range-picker
            v-model:value="searchForm.dateRange"
            format="YYYY-MM-DD"
            value-format="YYYYMMDD"
            style="width: 240px"
            :disabled-date="disabledDate"
          />
        </a-form-item>
        
        <a-form-item>
          <a-button type="primary" html-type="submit" :loading="loading">
            <template #icon>
              <SearchOutlined />
            </template>
            搜索
          </a-button>
          <a-button style="margin-left: 8px" @click="handleReset">
            重置
          </a-button>
          <a-button style="margin-left: 8px" @click="handleExport" :loading="exportLoading">
            <template #icon>
              <DownloadOutlined />
            </template>
            导出Excel
          </a-button>
        </a-form-item>
      </a-form>
    </a-card>
    
    <a-card>
      <!-- 固定的虚拟水平滚动条 -->
      <div 
        v-if="flatStatisticsList.length > 0"
        class="virtual-scrollbar sticky-scrollbar"
        ref="virtualScrollbar"
        @scroll="onVirtualScroll"
      >
        <div class="virtual-scrollbar-content"></div>
      </div>
      
      <div class="table-wrapper" ref="tableWrapper">
        <a-table
          :columns="columns"
          :data-source="flatStatisticsList"
          :pagination="false"
          :loading="loading"
          :row-key="getRowKey"
          :scroll="{ x: 1340 }"
          :sticky="{ offsetHeader: 24 }"
          size="middle"
          bordered
          ref="antTable"
        >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'color'">
            <span style="font-weight: 500;">{{ record.color || '-' }}</span>
          </template>
          
          <template v-else-if="column.key === 'size'">
            <span style="font-weight: 500;">{{ record.size || '-' }}</span>
          </template>
          
          <template v-else-if="column.key === 'inStock'">
            <span style="color: #52c41a; font-weight: 500;">{{ record.inStock || 0 }}</span>
          </template>
          
          <template v-else-if="column.key === 'outStock'">
            <span style="color: #ff4d4f; font-weight: 500;">{{ record.outStock || 0 }}</span>
          </template>

          <template v-else-if="column.key === 'netSalesStock'">
            <span :style="`color: ${(record.netSalesStock || 0) > 0 ? '#52c41a' : (record.netSalesStock || 0) === 0 ? '#666' : '#ff4d4f'}; font-weight: 500;`">
              {{ record.netSalesStock || 0 }}
            </span>
          </template>

          <template v-else-if="column.key === 'styleReturnRate'"> 
            <div style="text-align: center; font-size: 12px; line-height: 1.4;">
              <span style="color: #666;">退货(</span>
              <span style="color: #fa8c16; font-weight: 500;">{{ record.styleReturnStock || 0 }}</span>
              <span style="color: #666;">)/销售(</span>
              <span style="color: #1890ff; font-weight: 500;">{{ record.styleSalesStock || 0 }}</span>
              <span style="color: #666;">)=</span>
              <span style="font-weight: 500;">{{ record.returnRate || '0%' }}</span>
            </div>
          </template>
        </template>
        </a-table>
      </div>
    </a-card>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, onMounted, onUnmounted, nextTick, h } from 'vue'
import { message } from 'ant-design-vue'
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons-vue'
import { stockApi, productApi } from '@/api'
import { thumbUrl } from '@/utils/image'
import dayjs from 'dayjs'

export default {
  name: 'StockStatistics',
  components: {
    SearchOutlined,
    DownloadOutlined
  },
  setup() {
    const loading = ref(false)
    const summaryLoading = ref(false)
    const exportLoading = ref(false)
    // 纯展示、整体替换的大数据用 shallowRef，避免 Vue 对每个嵌套属性建响应式依赖链（大数据时会拖垮主线程/GC 导致卡死）
    const statisticsList = shallowRef([])
    const flatStatisticsList = shallowRef([])
    const categories = ref([])
    const summaryData = ref({})
    
    const searchForm = reactive({
      productName: '',
      reasonType: undefined,
      dateRange: undefined
    })
    
    
    const columns = [
      {
        title: '商品图片',
        key: 'image',
        width: 50,
        align: 'center',
        fixed: 'left',
        customRender: ({ record }) => {
          const obj = {
            children: null,
            props: {}
          }
          
          if (record.styleIndex === 0) {
            obj.props.rowSpan = record.totalStyles
            if (record.imageUrls && record.imageUrls.length > 0) {
              obj.children = h('div', { class: 'image-container' }, [
                h('img', {
                  src: thumbUrl(record.imageUrls[0]),
                  style: {
                    width: '50px',
                    height: '50px',
                    borderRadius: '4px',
                    objectFit: 'cover',
                    display: 'block'
                  },
                  onError: (e) => {
                    e.target.style.display = 'none'
                    e.target.parentNode.innerHTML = '<div class="no-image">暂无图片</div>'
                  }
                })
              ])
            } else {
              obj.children = h('div', { class: 'image-container' }, [
                h('div', { class: 'no-image' }, '暂无图片')
              ])
            }
          } else {
            obj.props.rowSpan = 0
          }
          
          return obj
        }
      },
      {
        title: '商品',
        key: 'productName',
        width: 120,
        ellipsis: true,
        fixed: 'left',
        customRender: ({ record }) => {
          const obj = {
            children: null,
            props: {}
          }
          
          if (record.styleIndex === 0) {
            obj.props.rowSpan = record.totalStyles
            obj.children = h('span', { style: 'font-weight: 500;' }, record.productName)
          } else {
            obj.props.rowSpan = 0
          }
          
          return obj
        }
      },
      {
        title: '总入库',
        key: 'totalInStock',
        width: 50,
        align: 'center',
        customRender: ({ record }) => {
          const obj = {
            children: null,
            props: {}
          }

          if (record.styleIndex === 0) {
            obj.props.rowSpan = record.totalStyles
            obj.children = h('span', { style: 'color: #52c41a; font-weight: 500;' }, record.totalInStock || 0)
          } else {
            obj.props.rowSpan = 0
          }

          return obj
        }
      },
      {
        title: '生产入库',
        key: 'productionStock',
        width: 50,
        align: 'center',
        customRender: ({ record }) => {
          const obj = {
            children: null,
            props: {}
          }

          if (record.styleIndex === 0) {
            obj.props.rowSpan = record.totalStyles
            obj.children = h('span', { style: 'color: #52c41a; font-weight: 500;' }, record.productionStock || 0)
          } else {
            obj.props.rowSpan = 0
          }

          return obj
        }
      },
      {
        title: '总出库',
        key: 'totalOutStock',
        width: 50,
        align: 'center',
        customRender: ({ record }) => {
          const obj = {
            children: null,
            props: {}
          }

          if (record.styleIndex === 0) {
            obj.props.rowSpan = record.totalStyles
            obj.children = h('span', { style: 'color: #ff4d4f; font-weight: 500;' }, record.totalOutStock || 0)
          } else {
            obj.props.rowSpan = 0
          }

          return obj
        }
      },
      {
        title: '总净销售数',
        key: 'totalNetSalesStock',
        width: 60,
        align: 'center',
        customRender: ({ record }) => {
          const obj = {
            children: null,
            props: {}
          }

          if (record.styleIndex === 0) {
            obj.props.rowSpan = record.totalStyles
            const totalNetSalesStock = record.totalNetSalesStock || 0
            obj.children = h('span', {
              style: `color: ${totalNetSalesStock > 0 ? '#52c41a' : totalNetSalesStock === 0 ? '#666' : '#ff4d4f'}; font-weight: 500;`
            }, totalNetSalesStock)
          } else {
            obj.props.rowSpan = 0
          }

          return obj
        }
      },
      {
        title: '总退货率(退货入库/销售出库)',
        key: 'totalReturnRate',
        width: 160,
        align: 'center',
        customRender: ({ record }) => {
          const obj = {
            children: null,
            props: {}
          }

          if (record.styleIndex === 0) {
            obj.props.rowSpan = record.totalStyles
            const returnStock = record.returnStock || 0
            const salesStock = record.salesStock || 0
            const totalReturnRate = record.totalReturnRate || '0%'

            obj.children = h('div', { style: 'text-align: center; font-size: 12px; line-height: 1.4;' }, [
              h('span', { style: 'color: #666;' }, '退货('),
              h('span', { style: 'color: #fa8c16; font-weight: 500;' }, returnStock),
              h('span', { style: 'color: #666;' }, ')/销售('),
              h('span', { style: 'color: #1890ff; font-weight: 500;' }, salesStock),
              h('span', { style: 'color: #666;' }, ')='),
              h('a-tag', {
                color: getReturnRateColor(totalReturnRate),
                style: 'font-weight: 500; font-size: 12px;'
              }, totalReturnRate)
            ])
          } else {
            obj.props.rowSpan = 0
          }

          return obj
        }
      },
      {
        title: '颜色',
        key: 'color',
        width: 60,
        align: 'center'
      },
      {
        title: '大小',
        key: 'size',
        width: 60,
        align: 'center'
      },
      {
        title: '入库',
        key: 'inStock',
        width: 60,
        align: 'center'
      },
      {
        title: '出库',
        key: 'outStock',
        width: 60,
        align: 'center'
      },
      {
        title: '净销售数',
        key: 'netSalesStock',
        width: 70,
        align: 'center'
      },
      {
        title: '退货率(退货入库/销售出库)',
        key: 'styleReturnRate',
        width: 160,
        align: 'center'
      }
    ]
    
    const loadStatistics = async () => {
      loading.value = true
      try {
        const params = {}
        
        // 添加商品名称搜索参数
        if (searchForm.productName && searchForm.productName.trim()) {
          params.productName = searchForm.productName.trim()
        }
        
        // 处理原因类型筛选
        if (searchForm.reasonType) {
          if (searchForm.reasonType === 'other_in') {
            params.reasonType = 'other'
            params.movementType = 'IN'
          } else if (searchForm.reasonType === 'other_out') {
            params.reasonType = 'other'
            params.movementType = 'OUT'
          } else {
            params.reasonType = searchForm.reasonType
          }
        }
        
        // 处理日期范围
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startTime = searchForm.dateRange[0]
          params.endTime = searchForm.dateRange[1]
        }
        
        const result = await stockApi.getStatistics(params)
        statisticsList.value = result.data || []
        
        // 将数据转换为扁平化结构
        flatStatisticsList.value = flattenStatisticsData(statisticsList.value)
      } catch (error) {
        console.error('加载出入库统计失败:', error)
        message.error('加载出入库统计失败')
      } finally {
        loading.value = false
      }
    }
    
    const loadSummary = async () => {
      summaryLoading.value = true
      try {
        const params = {}
        
        // 添加商品名称搜索参数
        if (searchForm.productName && searchForm.productName.trim()) {
          params.productName = searchForm.productName.trim()
        }
        
        // 处理原因类型筛选
        if (searchForm.reasonType) {
          if (searchForm.reasonType === 'other_in') {
            params.reasonType = 'other'
            params.movementType = 'IN'
          } else if (searchForm.reasonType === 'other_out') {
            params.reasonType = 'other'
            params.movementType = 'OUT'
          } else {
            params.reasonType = searchForm.reasonType
          }
        }
        
        // 处理日期范围
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startTime = searchForm.dateRange[0]
          params.endTime = searchForm.dateRange[1]
        }
        
        const result = await stockApi.getStatisticsSum(params)
        summaryData.value = result.data || {}
      } catch (error) {
        console.error('加载统计汇总失败:', error)
        message.error('加载统计汇总失败')
      } finally {
        summaryLoading.value = false
      }
    }
    
    const loadCategories = async () => {
      if (categories.value.length > 0) return
      
      try {
        const result = await productApi.getCategoryTree()
        const flatCategories = []
        
        const flattenCategories = (cats, prefix = '') => {
          cats.forEach(cat => {
            flatCategories.push({
              id: cat.id,
              name: prefix + cat.name
            })
            if (cat.children && cat.children.length > 0) {
              flattenCategories(cat.children, prefix + cat.name + ' / ')
            }
          })
        }
        
        flattenCategories(result.data)
        categories.value = flatCategories
      } catch (error) {
        console.error('加载分类失败:', error)
      }
    }
    
    const handleSearch = () => {
      loadStatistics()
      loadSummary()
    }
    
    const handleReset = () => {
      Object.assign(searchForm, {
        productName: '',
        reasonType: undefined,
        dateRange: undefined
      })
      loadStatistics()
      loadSummary()
    }

    const handleExport = async () => {
      exportLoading.value = true
      try {
        const params = {}

        // 添加商品名称搜索参数
        if (searchForm.productName && searchForm.productName.trim()) {
          params.productName = searchForm.productName.trim()
        }

        // 处理原因类型筛选
        if (searchForm.reasonType) {
          if (searchForm.reasonType === 'other_in') {
            params.reasonType = 'other'
            params.movementType = 'IN'
          } else if (searchForm.reasonType === 'other_out') {
            params.reasonType = 'other'
            params.movementType = 'OUT'
          } else {
            params.reasonType = searchForm.reasonType
          }
        }

        // 处理日期范围
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startTime = searchForm.dateRange[0]
          params.endTime = searchForm.dateRange[1]
        }

        const response = await stockApi.exportStatistics(params)

        // 创建下载链接
        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `出入库统计_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)

        message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        message.error('导出失败')
      } finally {
        exportLoading.value = false
      }
    }

    const disabledDate = (current) => {
      // 只限制不能选择未来的日期
      return current && current > dayjs().endOf('day')
    }
    
    const getReturnRateColor = (returnRate) => {
      if (!returnRate || returnRate === '0%') return 'green'
      if (returnRate === '∞') return 'red'
      
      const rate = parseFloat(returnRate.replace('%', ''))
      if (rate <= 5) return 'green'
      if (rate <= 15) return 'orange'
      return 'red'
    }
    
    // 将统计数据扁平化为表格行
    const flattenStatisticsData = (data) => {
      const flatData = []
      
      data.forEach(product => {
        const styleStatistics = product.styleStatistics || []
        const maxRows = Math.max(1, styleStatistics.length)
        
        for (let i = 0; i < maxRows; i++) {
          const style = styleStatistics[i] || {}
          flatData.push({
            // 商品基本信息（只在第一行显示）
            isMainRow: i === 0,
            productId: product.productId,
            productName: product.productName,
            imageUrls: product.imageUrls,
            totalInStock: product.totalInStock,
            productionStock: product.productionStock,
            totalOutStock: product.totalOutStock,
            totalReturnRate: product.totalReturnRate,
            returnStock: product.returnStock,
            salesStock: product.salesStock,
            totalNetSalesStock: product.netSalesStock || ((product.salesStock || 0) - (product.returnStock || 0)),

            // 款式信息（每行都显示）
            color: style.color,
            size: style.size,
            inStock: style.inStock,
            outStock: style.outStock,
            returnRate: style.returnRate,
            styleReturnStock: style.returnStock,
            styleSalesStock: style.salesStock,
            netSalesStock: style.netSalesStock || ((style.salesStock || 0) - (style.returnStock || 0)),
            
            // 用于行key和合并单元格
            rowIndex: flatData.length,
            styleIndex: i,
            totalStyles: maxRows
          })
        }
      })
      
      return flatData
    }
    
    // 生成行key
    const getRowKey = (record) => {
      return `${record.productId}-${record.styleIndex}`
    }

    const virtualScrollbar = ref(null)
    const antTable = ref(null)
    let tableScrollElement = null
    let isVirtualScrolling = false
    let isTableScrolling = false

    const onVirtualScroll = () => {
      if (isTableScrolling) return
      isVirtualScrolling = true
      
      if (virtualScrollbar.value && tableScrollElement) {
        tableScrollElement.scrollLeft = virtualScrollbar.value.scrollLeft
      }
      
      setTimeout(() => {
        isVirtualScrolling = false
      }, 10)
    }

    const onTableScroll = () => {
      if (isVirtualScrolling) return
      isTableScrolling = true
      
      if (virtualScrollbar.value && tableScrollElement) {
        virtualScrollbar.value.scrollLeft = tableScrollElement.scrollLeft
      }
      
      setTimeout(() => {
        isTableScrolling = false
      }, 10)
    }

    const initScrollSync = () => {
      if (antTable.value && antTable.value.$el) {
        // 尝试多个可能的滚动元素选择器
        const selectors = [
          '.ant-table-body', 
          '.ant-table-container', 
          '.ant-table-content',
          '.ant-table-tbody'
        ]
        
        for (const selector of selectors) {
          const element = antTable.value.$el.querySelector(selector)
          if (element) {
            // 检查元素是否真的可以滚动
            if (element.scrollWidth > element.clientWidth) {
              tableScrollElement = element
              tableScrollElement.addEventListener('scroll', onTableScroll)
              break
            }
          }
        }
        
        if (!tableScrollElement) {
          // 强制使用第一个找到的表格体元素
          const bodyElement = antTable.value.$el.querySelector('.ant-table-body')
          if (bodyElement) {
            tableScrollElement = bodyElement
            tableScrollElement.addEventListener('scroll', onTableScroll)
          }
        }
      }
    }

    onMounted(async () => {
      await loadStatistics()
      await loadSummary()
      
      // 等待DOM更新完成
      await nextTick()
      setTimeout(initScrollSync, 500)
    })

    onUnmounted(() => {
      if (tableScrollElement) {
        tableScrollElement.removeEventListener('scroll', onTableScroll)
      }
    })
    
    return {
      loading,
      summaryLoading,
      exportLoading,
      statisticsList,
      flatStatisticsList,
      categories,
      summaryData,
      searchForm,
      columns,
      handleSearch,
      handleReset,
      handleExport,
      loadCategories,
      disabledDate,
      getReturnRateColor,
      flattenStatisticsData,
      getRowKey,
      virtualScrollbar,
      antTable,
      onVirtualScroll
    }
  }
}
</script>

<style scoped>
.stock-statistics {
  padding: 0;
  /* 优化布局以减少ResizeObserver触发 */
  contain: layout style;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.summary-card {
  margin-bottom: 16px;
}

.filter-card {
  margin-bottom: 16px;
}

.search-form .ant-form-item {
  margin-bottom: 0;
}

.image-container {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.no-image {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  font-size: 10px;
  color: #999;
  flex-shrink: 0;
}

/* 优化表格性能 */
:deep(.ant-table) {
  contain: layout;
}

:deep(.ant-table-tbody > tr > td) {
  will-change: auto;
}

:deep(.ant-table-thead > tr > th) {
  contain: layout;
}

/* 减少不必要的重绘 */
:deep(.ant-table-container) {
  contain: layout style;
}

:deep(.ant-table-body) {
  contain: layout;
}

/* 统计卡片样式 */
.stat-section {
  padding: 8px 0;
}

.stat-details {
  padding: 0;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 13px;
}

.detail-label {
  color: #666;
  font-weight: 400;
}

.detail-value {
  font-weight: 500;
}

.virtual-scrollbar {
  width: 100%;
  height: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

.sticky-scrollbar {
  position: sticky;
  top: 0;
  z-index: 11;
  background: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table-wrapper {
  position: relative;
}

.virtual-scrollbar-content {
  width: 1340px;
  height: 1px;
}

.virtual-scrollbar::-webkit-scrollbar {
  height: 12px;
}

.virtual-scrollbar::-webkit-scrollbar-track {
  background: #f8f9fa;
  border-radius: 4px;
}

.virtual-scrollbar::-webkit-scrollbar-thumb {
  background: #ced4da;
  border-radius: 4px;
}

.virtual-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #adb5bd;
}
</style>